import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Divider, 
  Button, 
  CircularProgress, 
  Alert,
  Chip,
  Card,
  CardContent
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Visibility as EyeIcon,
  Note as NoteIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { getDatabase, ref, get } from 'firebase/database';
import { useAuth } from '../contexts/AuthContext';
import { TestResult } from '../types/testTypes';
import AmslerGridCardCanvas from './AmslerGridCardCanvas';
import { useFirebaseService } from '../contexts/FirebaseServiceContext';
import TestResultDialog from './TestResultDialog';
import { downloadTestResultImage, shareTestResult, printTestResult } from '../utils/testResultUtils';
import { GridDot } from '../types/AmslerGridData';

interface TestResultDetailProps {
  testId: string;
}

const TestResultDetail: React.FC<TestResultDetailProps> = ({ testId }) => {
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { currentUser } = useAuth();
  const { firebaseService } = useFirebaseService();

  useEffect(() => {
    const fetchTestResult = async () => {
      if (!testId || !currentUser) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Fetch from Firebase Realtime Database
        const db = getDatabase();
        const testRef = ref(db, `users/${currentUser.uid}/grids/${testId}`);
        
        const snapshot = await get(testRef);
        
        if (!snapshot.exists()) {
          setError('Test result not found');
          setLoading(false);
          return;
        }
        
        const data = snapshot.val();
        
        // Verify that the test belongs to the current user
        if (data.userId !== currentUser.uid) {
          setError('You do not have permission to view this test result');
          setLoading(false);
          return;
        }
        
        setTestResult({
          id: testId,
          userId: data.userId,
          timestamp: data.timestamp,
          eye: data.eyeTested,
          markedPoints: data.dots || data.markedPoints || [],
          abnormalities: data.abnormalities || [],
          notes: data.notes || '',
        });
      } catch (err) {
        console.error('Error fetching test result:', err);
        setError('Failed to load test result. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTestResult();
  }, [testId, currentUser]);

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Unknown date';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return format(date, 'PPpp'); // Format: Mar 15, 2023, 3:45 PM
    } catch (err) {
      console.error('Error formatting date:', err);
      return 'Invalid date';
    }
  };

  const handleOpenFullSizeView = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleShare = () => {
    if (!testResult) return;
    
    const testData = {
      timestamp: testResult.timestamp,
      eyeTested: testResult.eye,
      dots: testResult.markedPoints,
      abnormalities: testResult.abnormalities,
      notes: testResult.notes
    };
    
    shareTestResult(testData);
  };

  const handleDownload = () => {
    if (!testResult) return;
    
    const testData = {
      timestamp: testResult.timestamp,
      eyeTested: testResult.eye,
      dots: testResult.markedPoints,
      abnormalities: testResult.abnormalities,
      notes: testResult.notes
    };
    
    downloadTestResultImage(testData);
  };

  const handlePrint = () => {
    if (!testResult) return;
    
    const testData = {
      timestamp: testResult.timestamp,
      eyeTested: testResult.eye,
      dots: testResult.markedPoints,
      abnormalities: testResult.abnormalities,
      notes: testResult.notes
    };
    
    printTestResult(testData);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !testResult) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error || 'Unable to load test result'}
      </Alert>
    );
  }

  return (
    <Box>
      <Paper elevation={2} sx={{ mb: 3, overflow: 'hidden' }}>
        <Box 
          p={3} 
          sx={{ 
            bgcolor: 'primary.main', 
            color: 'primary.contrastText',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Box>
            <Typography variant="h5" component="h1" gutterBottom={false}>
              {testResult.eye === 'left' ? 'Left Eye Test' : 'Right Eye Test'}
            </Typography>
            <Box display="flex" alignItems="center" mt={0.5}>
              <CalendarIcon fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2">
                {formatDate(testResult.timestamp)}
              </Typography>
            </Box>
          </Box>
          
          <Box>
            {testResult.abnormalities && testResult.abnormalities.length > 0 && (
              <Chip 
                label="Abnormalities Detected" 
                color="error" 
                variant="filled"
                sx={{ 
                  fontWeight: 'bold',
                  '& .MuiChip-label': { color: 'white' }
                }} 
              />
            )}
          </Box>
        </Box>
        
        <Box p={3}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Test Visualization
              </Typography>
              
              <Card 
                variant="outlined" 
                sx={{ 
                  mb: 3, 
                  height: 350, 
                  width: '100%',
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  '&:hover': {
                    boxShadow: 3,
                    transform: 'scale(1.01)',
                    transition: 'all 0.2s ease'
                  }
                }}
                onClick={handleOpenFullSizeView}
              >
                {testResult.markedPoints && testResult.markedPoints.length > 0 ? (
                  <Box sx={{ 
                    width: '100%', 
                    height: '100%', 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    padding: 2
                  }}>
                    <AmslerGridCardCanvas 
                      dots={testResult.markedPoints.map(point => ({
                        x: point.x,
                        y: point.y,
                        timestamp: testResult.timestamp
                      }))}
                      curves={[]}
                      width={300}
                      height={300}
                    />
                  </Box>
                ) : (
                  <Typography color="text.secondary" align="center">
                    No abnormalities were marked on the grid
                  </Typography>
                )}
              </Card>
              
              <Box display="flex" gap={1} justifyContent="center">
                <Button 
                  variant="outlined" 
                  size="small" 
                  startIcon={<PrintIcon />}
                  onClick={handlePrint}
                >
                  Print
                </Button>
                <Button 
                  variant="outlined" 
                  size="small" 
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                >
                  Download
                </Button>
                <Button 
                  variant="outlined" 
                  size="small" 
                  startIcon={<ShareIcon />}
                  onClick={handleShare}
                >
                  Share
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Test Details
              </Typography>
              
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <EyeIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="body1">
                      <strong>Eye Tested:</strong> {testResult.eye === 'left' ? 'Left Eye' : 'Right Eye'}
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Box mb={2}>
                    <Typography variant="body1" gutterBottom>
                      <strong>Abnormalities Detected:</strong>
                    </Typography>
                    
                    {testResult.abnormalities && testResult.abnormalities.length > 0 ? (
                      <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                        {testResult.abnormalities.map((abnormality, index) => (
                          <Chip 
                            key={index} 
                            label={abnormality} 
                            color="error" 
                            size="small"
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography color="text.secondary">
                        No abnormalities detected
                      </Typography>
                    )}
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Box>
                    <Typography variant="body1" gutterBottom display="flex" alignItems="center">
                      <NoteIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <strong>Notes:</strong>
                    </Typography>
                    
                    {testResult.notes ? (
                      <Typography variant="body2" sx={{ mt: 1, px: 1 }}>
                        {testResult.notes}
                      </Typography>
                    ) : (
                      <Typography color="text.secondary">
                        No notes for this test
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
              
              <Alert severity="info">
                If you notice changes in your vision, please consult with an eye care professional.
              </Alert>
            </Grid>
          </Grid>
        </Box>
      </Paper>
      
      <Box display="flex" justifyContent="center">
        <Button 
          variant="contained" 
          color="primary"
          href="/test"
        >
          Take a New Test
        </Button>
      </Box>
      
      {/* Test Result Dialog for full-size view */}
      <TestResultDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        testData={testResult ? {
          timestamp: testResult.timestamp,
          eyeTested: testResult.eye,
          dots: testResult.markedPoints ? testResult.markedPoints.map(point => {
            // Create a new dot object with required fields
            const dot: any = {
              x: point.x,
              y: point.y,
              timestamp: testResult.timestamp
            };
            
            // Add optional fields if they exist
            if ('intensity' in point && point.intensity !== undefined) {
              dot.intensity = point.intensity;
            }
            
            if ('distortionRadius' in point && point.distortionRadius !== undefined) {
              dot.distortionRadius = point.distortionRadius;
            }
            
            return dot;
          }) : [],
          abnormalities: testResult.abnormalities,
          notes: testResult.notes
        } : null}
        onShare={handleShare}
        onDownload={handleDownload}
        onPrint={handlePrint}
      />
    </Box>
  );
};

export default TestResultDetail; 